import React from "react";

/**
 * Saying so when the collection is not actually being kept in step.
 *
 * This exists because of a day it did not. An invalid key on the server meant
 * every sync came back refused; the hook caught it, set an error state, and
 * nothing anywhere rendered that state — so the app looked exactly like an app
 * that was working. A phone showed empty lists, a laptop's submissions never
 * left it, and the only place the truth was written down was a server log
 * nobody had a reason to read.
 *
 * The failure itself was fair enough. Software talks to servers that say no.
 * What made it cost a day was that the app knew and did not mention it.
 *
 * So: quiet when there is nothing to say, and never in the way — this is a
 * note, not a modal. The app has always worked with everything in one browser
 * and still does, which is exactly why the message is "not syncing" rather
 * than "something went wrong". Nothing is lost either way; it is only failing
 * to be in two places, and that is what it says.
 */

export default function SyncStatus({ state, paused, onReopen, onRetry }) {
  /* Order matters. A pending conflict is also why syncing stopped, and being
     told to retry a thing that is waiting on you would be a wrong answer. */
  if (paused) {
    return (
      <div className="sync-status sync-status-ask" role="status">
        <span>
          <strong>Your devices are out of step.</strong> This device and your
          account have different collections — nothing syncs until you pick one.
        </span>
        <button type="button" className="btn btn-sm btn-primary" onClick={onReopen}>
          Pick one
        </button>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="sync-status sync-status-error" role="status">
        <span>
          <strong>Not syncing.</strong> Couldn't reach your account. Your cards
          are safe on this device, but changes aren't reaching your other ones.
        </span>
        <button type="button" className="btn btn-sm btn-secondary" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }

  return null;
}
